import { useEffect } from 'react'
import { useLiveQuery } from '@tanstack/react-db'

import { useDb } from '@/lib/dbContext'
import type { Branding } from '@/lib/schemas/db'

/** Product name, used only when no branding row exists at all. */
export const FALLBACK_BRAND_NAME = 'JobCard'

export type BrandInfo = {
  /** Trading name; empty string until the branding row has loaded. */
  name: string
  /** Logo for dark backgrounds, when one is uploaded. */
  logoUrl?: string
  /** Logo for light backgrounds; falls back to `logoUrl`. */
  logoOnLightUrl?: string
  /** False while the branding row is still loading. */
  isReady: boolean
}

/** "Log in to <shop>" — or a bare "Log in" until the name is known. */
export function loginTitle(brandName: string) {
  return brandName ? `Log in to ${brandName}` : 'Log in'
}

export function toBrandInfo(
  branding: Branding | undefined,
  isReady: boolean,
): BrandInfo {
  if (!branding) {
    return { name: isReady ? FALLBACK_BRAND_NAME : '', isReady }
  }
  return {
    name: branding.name || FALLBACK_BRAND_NAME,
    logoUrl: branding.logoUrl,
    logoOnLightUrl: branding.logoOnLightUrl ?? branding.logoUrl,
    isReady,
  }
}

/**
 * The shop's branding (name + logos) from the `branding` singleton. Readable
 * before sign-in, so the login and kiosk screens can use it too.
 */
export function useBranding(): BrandInfo {
  const db = useDb()
  const result = useLiveQuery((q) => q.from({ branding: db.brandingCollection }))

  useEffect(() => {
    void db.brandingCollection.preload?.()
  }, [db])

  return toBrandInfo(result.data?.[0], result.isReady)
}
